import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import { SessionFile } from "../../types/system/SessionFile";
import Datastore from "../../communication/Datastore";
import User from "../../types/core/User";
import PermissionSet from "../../types/core/PermissionSet";
import store, { IAppState } from "../Store";
import Utilities from "../../foundation/Utility";
import {
    fetchTwoFactorDetails,
    fetchUserContactDetails,
    updateUserProfilePicture,
} from "../core/User";

// Define the type for our state store entry
type SessionState = {
    user: User;
    profilePicture: string;
    permissions: PermissionSet;
    file: SessionFile;
    status: string;
    error: string;
};

// Define the initial state for our state store entry
const initialState: SessionState = {
    user: null,
    profilePicture: null,
    permissions: null,
    file: null,
    status: "idle",
    error: null,
};

// Create a slice to hold all our reducers
const sessionSlice = createSlice({
    name: "session",
    initialState: initialState,
    reducers: {
        updateSessionProfilePicture: (state, action: PayloadAction<string>) => {
            state.profilePicture = action.payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "session") return undefined;
                return {
                    user: rawData?.payload?.session?.user
                        ? new User(rawData?.payload?.session.user)
                        : null,
                    profilePicture: rawData?.payload?.session?.profilePicture
                        ? rawData?.payload?.session.profilePicture
                        : null,
                    permissions: rawData?.payload?.session?.permissions
                        ? new PermissionSet(
                              rawData?.payload?.session.permissions,
                          )
                        : null,
                    file: rawData?.payload?.session?.file
                        ? new SessionFile(rawData?.payload?.session.file)
                        : null,
                    status: !rawData?.payload?.session?.user
                        ? "idle"
                        : rawData?.payload?.session?.status,
                    error: null,
                };
            })
            .addCase(fetchUserDetails.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchUserDetails.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Add any fetched posts to the array
                state.user = new User(action.payload[0]);
                state.user.numbers = [];
                state.user.emails = [];
                state.user.addresses = [];
                state.user.fullName = [
                    state.user.firstName,
                    state.user.middleName,
                    state.user.lastName,
                ]
                    .filter(name => !!name)
                    .join(" ");
                state.profilePicture = state.user.profilePicture;
                state.permissions = action.payload[1];
            })
            .addCase(fetchUserDetails.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

// We have to export the reducer as this is added to the state store
export default sessionSlice.reducer;

export const { updateSessionProfilePicture } = sessionSlice.actions;

const fetchUserDetails = createAsyncThunk(
    "session/fetchUserDetails",
    async () => {
        const response = await Promise.all([
            Datastore.getType<User>("api/Authentication/Session/User"),
            Datastore.getType<PermissionSet>(
                "api/Authentication/Session/PermissionSet",
            ),
        ]);
        return response;
    },
);
export const initialiseUserDetails = createAsyncThunk(
    "session/initialiseUserDetails",
    async (_, thunkAPI) => {
        const details = await thunkAPI.dispatch(fetchUserDetails());
        thunkAPI.dispatch(
            updateUserProfilePicture(details.payload[0].profilePicture),
        );
    },
    {
        condition: (_, { getState }) => {
            const { session } = (getState() as IAppState).session;
            return session.status === "idle";
        },
    },
);
export const initialiseUserContactDetails = createAsyncThunk(
    "user/initialiseUserContactDetails",
    async (_, thunkAPI) => {
        await thunkAPI.dispatch(initialiseUserDetails());

        await Utilities.waitOn(
            () => store.getState().session.session.status === "succeeded",
        );

        const { session } = store.getState().session;
        const { user } = store.getState().db;

        if (user.contactStatus !== "loading") {
            thunkAPI.dispatch(
                updateUserProfilePicture(session.user.profilePicture),
            );
            await thunkAPI.dispatch(fetchUserContactDetails(session.user));
        }
    },
);
export const initialiseTwoFactorDetails = createAsyncThunk(
    "user/initialiseTwoFactorDetails",
    async (_, thunkAPI) => {
        await thunkAPI.dispatch(initialiseUserDetails());

        await Utilities.waitOn(
            () => store.getState().session.session.status === "succeeded",
        );

        const { session } = store.getState().session;
        await thunkAPI.dispatch(fetchTwoFactorDetails(session.user));
    },
);
